import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  Clear as ClearIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { useAuth } from "../../../contexts/auth";
import { baseAxios, Context } from "../../../utils/config";
import { useLocation } from "react-router-dom";
import {
  ROLE_SYSTEM_ADMIN,
  ROLE_SALON_ADMIN,
  ROLE_JDTECH_ADMIN,
} from "../../../utils/constants";
import { colors } from "../../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [15, 30, 50, 100];

const MARK_STYLE = {
  backgroundColor: "rgba(197, 160, 111, 0.45)",
  color: "inherit",
  borderRadius: 4,
};

const COLUMNS = [
  { id: "expand", label: "", sortable: false, minWidth: 52 },
  {
    id: "location_name",
    label: "Salon",
    field: "location_name",
    minWidth: 190,
  },
  { id: "user_name", label: "Stylist", field: "user_name", minWidth: 140 },
  {
    id: "transaction_client_name",
    label: "Client Name",
    field: "transaction_client_name",
    minWidth: 150,
  },
  {
    id: "transaction_start",
    label: "Date",
    field: "transaction_start",
    minWidth: 145,
  },
  { id: "product_cost", label: "Cost", field: "product_cost", minWidth: 115 },
  {
    id: "transaction_entry_product_amount",
    label: "Amount",
    field: "transaction_entry_product_amount",
    minWidth: 115,
  },
  {
    id: "transaction_waste",
    label: "Waste",
    field: "transaction_waste",
    minWidth: 110,
  },
];

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => t.length > 1)
    .map(escapeRegex);

  const phrase = q.includes(" ") ? escapeRegex(q) : null;
  const patterns = [phrase, ...tokens].filter(Boolean);
  if (!patterns.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${patterns.join("|")})`, "i");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-us", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString("en-us", {
    hour: `numeric`,
    minute: `2-digit`,
  });
}

function formatNumber(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return value ?? "";
  return number.toFixed(2);
}

function formatCurrency(value) {
  const number = Number(value);
  if (Number.isNaN(number) || number < 0.01) return "$0";
  return `$${number.toFixed(2)}`;
}

function gramsLabel(value) {
  const formatted = formatNumber(value);
  return formatted ? `${formatted} g` : "-";
}

function groupChildrenIntoBowls(children) {
  const bowls = [];
  const indexByName = new Map();
  for (const child of children) {
    const name = child.bowl_name || "Bowl 1";
    let bowl = indexByName.has(name) ? bowls[indexByName.get(name)] : null;
    if (!bowl) {
      bowl = {
        name,
        time:
          child.bowl_time ||
          child.transaction_entry_start ||
          child.transaction_start,
        products: [],
      };
      indexByName.set(name, bowls.length);
      bowls.push(bowl);
    }
    bowl.products.push(child);
  }
  return bowls;
}

export default function RolledReports() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);
  const { state } = useLocation();
  const { clientName = "", location_name = "" } = state || {};
  const isAdmin = user.userRoles.some(
    (role) =>
      role === ROLE_SYSTEM_ADMIN ||
      role === ROLE_SALON_ADMIN ||
      role === ROLE_JDTECH_ADMIN,
  );
  const stylistId = user.userID || user.id;
  const initialSearch = useMemo(
    () => [clientName, location_name].filter(Boolean).join(" "),
    [clientName, location_name],
  );

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("transaction_start");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [expanded, setExpanded] = useState({});

  const selectedLocationId = globalLocation?.location_id;
  const selectedLocationName =
    selectedLocationId && selectedLocationId !== -1
      ? globalLocation?.location_name
      : "";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchRolledTransactions = useCallback(
    (nextPage, nextLimit, orderBy = sortBy, order = sortOrder) => {
      setLoading(true);
      setPage(nextPage);
      setRowsPerPage(nextLimit);
      const params = {
        page: nextPage,
        limit: nextLimit,
        sortBy: orderBy,
        sortOrder: order,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (!isAdmin && stylistId) params.stylistId = stylistId;

      baseAxios
        .get("/transactions/rolled", { params })
        .then((res) => {
          setRows(res?.data?.Items ?? []);
          setTotalCount(res?.data?.totalCount ?? 0);
          setExpanded({});
        })
        .catch(() => {
          setRows([]);
          setTotalCount(0);
          toast.error("Error loading rolled transactions", { duration: 3000 });
        })
        .finally(() => setLoading(false));
    },
    [sortBy, sortOrder, debouncedSearch, isAdmin, stylistId],
  );

  useEffect(() => {
    fetchRolledTransactions(0, rowsPerPage);
  }, [debouncedSearch, selectedLocationId]);

  const handleRefresh = useCallback(() => {
    fetchRolledTransactions(page, rowsPerPage);
  }, [fetchRolledTransactions, page, rowsPerPage]);

  const handleExport = useCallback(async () => {
    if (!rows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Rolled Transactions");
      worksheet.columns = [
        { header: "Salon", key: "location_name", width: 20 },
        { header: "Organization", key: "org_name", width: 20 },
        { header: "Stylist", key: "user_name", width: 18 },
        { header: "Client Name", key: "transaction_client_name", width: 18 },
        { header: "Date", key: "date", width: 12 },
        { header: "Time", key: "time", width: 12 },
        { header: "Cost", key: "product_cost", width: 12 },
        { header: "Amount (g)", key: "amount", width: 12 },
        { header: "Waste (g)", key: "waste", width: 12 },
        { header: "Bowl", key: "bowl", width: 12 },
        { header: "Product", key: "product", width: 28 },
        { header: "Line Amount (g)", key: "line_amount", width: 14 },
        { header: "Line Waste (g)", key: "line_waste", width: 14 },
        { header: "Line Cost", key: "line_cost", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      rows.forEach((row) => {
        const children = row.children ?? [];
        const base = {
          location_name: row.location_name || "",
          org_name: row.org_name || "",
          user_name: row.user_name || "",
          transaction_client_name: row.transaction_client_name || "",
          date: formatDate(row.transaction_start),
          time: formatTime(
            row.transaction_entry_start || row.transaction_start,
          ),
          product_cost: Number(row.product_cost) || 0,
          amount: Number(row.transaction_entry_product_amount) || 0,
          waste: Number(row.transaction_waste) || 0,
        };
        if (!children.length) {
          worksheet.addRow(base);
          return;
        }
        children.forEach((child) => {
          worksheet.addRow({
            ...base,
            bowl: child.bowl_name || "Bowl 1",
            product: child.product_alias || "",
            line_amount: Number(child.transaction_entry_product_amount) || 0,
            line_waste: Number(child.transaction_waste) || 0,
            line_cost: Number(child.product_cost) || 0,
          });
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `RolledTransactions_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [rows]);

  const handlePageChange = (_, value) => {
    fetchRolledTransactions(value - 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (e) => {
    fetchRolledTransactions(0, Number(e.target.value));
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortOrder(newOrder);
    fetchRolledTransactions(0, rowsPerPage, field, newOrder);
  };

  const toggleExpanded = (transactionId) => {
    const key = String(transactionId);
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage + rowsPerPage, totalCount);
  const tableMinWidth = COLUMNS.reduce(
    (sum, col) => sum + (col.minWidth || 0),
    0,
  );

  const renderDate = (row) => {
    const date = formatDate(row.transaction_start);
    const start = formatTime(
      row.transaction_entry_start || row.transaction_start,
    );
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <Typography component="span" fontWeight={500} fontSize={14}>
          <HighlightText text={date || "-"} query={debouncedSearch} />
        </Typography>
        {start ? (
          <Typography component="span" color="text.secondary" fontSize={12}>
            <HighlightText text={start} query={debouncedSearch} />
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderSalon = (row) => (
    <Box sx={{ py: 0.25 }}>
      <Typography fontWeight={500} fontSize={14} component="div">
        <HighlightText
          text={row.location_name || "-"}
          query={debouncedSearch}
        />
      </Typography>
      {row.org_name ? (
        <Typography
          fontSize={12}
          color="text.secondary"
          component="div"
          sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
        >
          <HighlightText text={row.org_name} query={debouncedSearch} />
        </Typography>
      ) : null}
    </Box>
  );

  const renderWaste = (value) => {
    const waste = Number(value);
    const hasWaste = !Number.isNaN(waste) && waste > 0;
    return (
      <Chip
        size="small"
        variant={hasWaste ? "filled" : "outlined"}
        label={
          <HighlightText text={gramsLabel(value)} query={debouncedSearch} />
        }
        sx={{
          height: 24,
          fontWeight: 600,
          bgcolor: hasWaste ? colors.gold : "transparent",
          color: hasWaste ? colors.black : "text.secondary",
          borderColor: hasWaste ? colors.gold : "divider",
          "&:hover": {
            bgcolor: hasWaste
              ? colors.gold
              : (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "transparent",
          },
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  };

  const renderCell = (row, col) => {
    if (col.id === "expand") {
      const children = row.children ?? [];
      const expandedKey = String(row.transaction_id);
      return (
        <IconButton
          size="small"
          aria-label={expanded[expandedKey] ? "Collapse row" : "Expand row"}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(row.transaction_id);
          }}
          disabled={!children.length}
        >
          {expanded[expandedKey] ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      );
    }
    if (col.id === "location_name") return renderSalon(row);
    if (col.id === "transaction_start") return renderDate(row);
    if (col.id === "product_cost") {
      return (
        <Typography component="span" fontWeight={600} fontSize={14}>
          <HighlightText
            text={formatCurrency(row.product_cost)}
            query={debouncedSearch}
          />
        </Typography>
      );
    }
    if (col.id === "transaction_entry_product_amount") {
      return (
        <HighlightText
          text={gramsLabel(row.transaction_entry_product_amount)}
          query={debouncedSearch}
        />
      );
    }
    if (col.id === "transaction_waste")
      return renderWaste(row.transaction_waste);

    const value = row[col.field];
    return value ? (
      <HighlightText text={String(value)} query={debouncedSearch} />
    ) : (
      "-"
    );
  };

  const renderChildRows = (row) => {
    const children = row.children ?? [];
    const expandedKey = String(row.transaction_id);
    if (!expanded[expandedKey] || !children.length) return null;
    const bowls = groupChildrenIntoBowls(children);

    return (
      <TableRow>
        <TableCell colSpan={COLUMNS.length} sx={{ p: 0, borderBottom: 0 }}>
          <Collapse in timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#141416" : "#f2f2f2",
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
                px: { xs: 2, md: 7 },
                py: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {bowls.map((bowl) => (
                <Box
                  key={bowl.name}
                  sx={{
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 2,
                      bgcolor: colors.black,
                      color: colors.white,
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <Typography fontWeight={700} fontSize={13}>
                      {bowl.name}
                    </Typography>
                    {bowl.time ? (
                      <Typography fontSize={12} sx={{ color: colors.gold }}>
                        {formatTime(bowl.time)}
                      </Typography>
                    ) : null}
                  </Box>
                  <Table
                    size="small"
                    aria-label={`${bowl.name} products`}
                    sx={{
                      "& th": {
                        bgcolor: "secondary.main",
                        color: "common.white",
                        fontSize: 12,
                        fontWeight: 700,
                        py: 0.75,
                        borderBottom: "1px solid",
                        borderColor: "primary.main",
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                      },
                      "& td": {
                        py: 1,
                        borderColor: "divider",
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell width={130}>Amount</TableCell>
                        <TableCell width={120}>Waste</TableCell>
                        <TableCell width={120}>Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bowl.products.map((child, index) => (
                        <TableRow
                          key={child.transaction_entry_id}
                          sx={{
                            "&:last-child td": { borderBottom: 0 },
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? index % 2 === 0
                                  ? "#1b1b1e"
                                  : "#141416"
                                : index % 2 === 0
                                  ? "white"
                                  : "#fcfcfd",
                          }}
                        >
                          <TableCell sx={{ maxWidth: 560 }}>
                            <Typography fontSize={13} fontWeight={500}>
                              <HighlightText
                                text={child.product_alias || "-"}
                                query={debouncedSearch}
                              />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13}>
                              <HighlightText
                                text={gramsLabel(
                                  child.transaction_entry_product_amount,
                                )}
                                query={debouncedSearch}
                              />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {renderWaste(child.transaction_waste)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              fontWeight={600}
                              fontSize={13}
                            >
                              <HighlightText
                                text={formatCurrency(child.product_cost)}
                                query={debouncedSearch}
                              />
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mt: 1.5,
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Rolled Transactions
            </Typography>
            {selectedLocationName ? (
              <Chip
                size="small"
                label={selectedLocationName}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            One row per visit. Expand it to see each bowl.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <IconButton
            aria-label="Refresh rolled transactions"
            onClick={handleRefresh}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "grey.100",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !rows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Export
          </Button>
          <TextField
            size="small"
            placeholder="Search salon, client, stylist, product, cost..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 340,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "grey.100" : "grey.800",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "6px",
          overflowX: "auto",
          overflowY: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Table
          aria-label="rolled transactions table"
          sx={{ width: "100%", minWidth: tableMinWidth }}
        >
          <TableHead
            sx={{
              bgcolor: "secondary.main",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              "& .MuiTableCell-head": {
                color: "common.white",
                fontWeight: 600,
                bgcolor: "secondary.main",
              },
              "& .MuiTableSortLabel-root": {
                color: "common.white",
                "&.Mui-active": { color: colors.gold },
                "&:hover": { color: colors.gold },
                "& .MuiTableSortLabel-icon": {
                  color: `${colors.gold} !important`,
                },
              },
            }}
          >
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{
                    minWidth: col.minWidth,
                    fontWeight: 600,
                    padding: "8px 8px 8px 16px",
                  }}
                >
                  {col.sortable === false || !col.field ? (
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                    >
                      {col.label}
                    </Typography>
                  ) : (
                    <TableSortLabel
                      active={sortBy === col.field}
                      direction={sortBy === col.field ? sortOrder : "asc"}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ p: "6px 4px 6px 16px" }}>
                      <Skeleton variant="text" sx={{ height: 40 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No data to display
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <React.Fragment key={row.transaction_id ?? `row-${i}`}>
                  <TableRow
                    onClick={() => {
                      if ((row.children ?? []).length) {
                        toggleExpanded(row.transaction_id);
                      }
                    }}
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#141416" : "background.paper",
                      cursor: (row.children ?? []).length
                        ? "pointer"
                        : "default",
                      "&:nth-of-type(4n-3)": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#1b1b1e" : "#fafafa",
                      },
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#2a2a2e" : "#f5f5f5",
                      },
                      "& td": { color: "text.primary", borderColor: "divider" },
                    }}
                  >
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          minWidth: col.minWidth,
                          padding: "6px 4px 6px 16px",
                        }}
                      >
                        {renderCell(row, col)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderChildRows(row)}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && totalCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Items per page:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ minWidth: 64, fontSize: "14px", height: "28px" }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2" color="text.secondary">
              {from}-{to} of {totalCount} transactions
            </Typography>
          </Box>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
